/**
 * SEO Manager - Gerenciamento de Meta Tags para SEO
 * Atualiza dinamicamente as meta tags de acordo com o conteúdo da página
 */

export const updateMetaTags = (config) => {
  const {
    title,
    description,
    keywords,
    canonical,
    robots = "index, follow",
    author = "Sensualle Lingerie Sexy",
    publisher = "Sensualle Lingerie Sexy",
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
  } = config;

  // Atualiza o título da página
  if (title) {
    document.title = title;
  }

  // Remove meta tags existentes para evitar duplicatas
  const metaTagsToRemove = [
    "description",
    "keywords",
    "robots",
    "author",
    "publisher",
    "og:title",
    "og:description",
    "og:image",
    "og:url",
    "og:type",
  ];

  metaTagsToRemove.forEach((name) => {
    const existing =
      document.querySelector(`meta[name="${name}"]`) ||
      document.querySelector(`meta[property="${name}"]`);
    if (existing) {
      existing.remove();
    }
  });

  // Remove canonical existente
  const existingCanonical = document.querySelector('link[rel="canonical"]');
  if (existingCanonical) {
    existingCanonical.remove();
  }

  // Adiciona meta description
  if (description) {
    const metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    metaDescription.content = truncateText(description, 155);
    document.head.appendChild(metaDescription);
  }

  // Adiciona meta keywords
  if (keywords) {
    const metaKeywords = document.createElement("meta");
    metaKeywords.name = "keywords";
    metaKeywords.content = keywords;
    document.head.appendChild(metaKeywords);
  }

  // Adiciona canonical
  if (canonical) {
    const linkCanonical = document.createElement("link");
    linkCanonical.rel = "canonical";
    linkCanonical.href = canonical;
    document.head.appendChild(linkCanonical);
  }

  // Adiciona robots
  const metaRobots = document.createElement("meta");
  metaRobots.name = "robots";
  metaRobots.content = robots;
  document.head.appendChild(metaRobots);

  // Adiciona author
  if (author) {
    const metaAuthor = document.createElement("meta");
    metaAuthor.name = "author";
    metaAuthor.content = author;
    document.head.appendChild(metaAuthor);
  }

  // Adiciona publisher
  if (publisher) {
    const metaPublisher = document.createElement("meta");
    metaPublisher.name = "publisher";
    metaPublisher.content = publisher;
    document.head.appendChild(metaPublisher);
  }

  // Open Graph Tags
  if (ogTitle || title) {
    const metaOgTitle = document.createElement("meta");
    metaOgTitle.setAttribute("property", "og:title");
    metaOgTitle.content = ogTitle || title;
    document.head.appendChild(metaOgTitle);
  }

  if (ogDescription || description) {
    const metaOgDescription = document.createElement("meta");
    metaOgDescription.setAttribute("property", "og:description");
    metaOgDescription.content = truncateText(ogDescription || description, 155);
    document.head.appendChild(metaOgDescription);
  }

  if (ogImage) {
    const metaOgImage = document.createElement("meta");
    metaOgImage.setAttribute("property", "og:image");
    metaOgImage.content = ogImage;
    document.head.appendChild(metaOgImage);
  }

  if (ogUrl || canonical) {
    const metaOgUrl = document.createElement("meta");
    metaOgUrl.setAttribute("property", "og:url");
    metaOgUrl.content = ogUrl || canonical;
    document.head.appendChild(metaOgUrl);
  }

  // og:type
  const metaOgType = document.createElement("meta");
  metaOgType.setAttribute("property", "og:type");
  metaOgType.content = "website";
  document.head.appendChild(metaOgType);
};

/**
 * Trunca texto para um número máximo de caracteres
 */
const truncateText = (text, maxLength) => {
  if (!text) return "";
  text = text.replace(/\n/g, " ").trim();
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3).trim() + "...";
};

/**
 * Gera keywords a partir de um texto
 */
export const generateKeywords = (text, additionalWords = []) => {
  if (!text) return "";

  // Remove pontuação e converte para minúsculas
  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter((word) => word.length > 3); // Remove palavras muito curtas

  // Remove duplicatas
  const uniqueWords = [...new Set([...words, ...additionalWords])];

  // Retorna as primeiras 20 palavras
  return uniqueWords.slice(0, 20).join(", ");
};

/**
 * Cria slug amigável para URLs
 */
export const createSlug = (text) => {
  if (!text) return "";

  return text
    .toLowerCase()
    .normalize("NFD") // Normaliza caracteres Unicode
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/[^\w\s-]/g, "") // Remove caracteres especiais
    .replace(/\s+/g, "-") // Substitui espaços por hífens
    .replace(/-+/g, "-") // Remove hífens duplicados
    .trim();
};

/**
 * Configuração SEO para página de produto
 */
export const generateProductSeo = (produto) => {
  if (!produto) return {};

  const baseUrl = window.location.origin;
  const slug = createSlug(produto.nomeproduto || produto.textlink || "");
  const canonical = `${baseUrl}/products/${slug}-${produto.codigo}`;

  // Usa MetaTagDescription se disponível, senão usa detalhes
  const description =
    produto.MetaTagDescription ||
    truncateText(produto.detalhes, 155) ||
    `Compre ${produto.nomeproduto} na Sensualle. Embalagem Discreta. 5% De Desconto No Pix. Até 6x Sem Juros.`;

  // Usa palavrassubstitutas se disponível, senão gera keywords
  const keywords =
    produto.palavrassubstitutas ||
    generateKeywords(
      `${produto.nomeproduto} ${produto.detalhes || ""} ${
        produto.Categoria || ""
      }`,
      ["sensualle", "lingerie", "sexy", produto.Marca || ""]
    );

  return {
    title: `${produto.nomeproduto} | Sensualle Lingerie Sexy`,
    description,
    keywords,
    canonical,
    robots: "index, follow",
    author: "Sensualle Lingerie Sexy",
    publisher: "Sensualle Lingerie Sexy",
    ogTitle: produto.nomeproduto,
    ogDescription: description,
    ogImage: produto.url,
    ogUrl: canonical,
  };
};

/**
 * Configuração SEO para página de categoria
 */
export const generateCategorySeo = (categoria, descricao = "") => {
  if (!categoria) return {};

  const baseUrl = window.location.origin;
  const slug = createSlug(categoria);
  const canonical = `${baseUrl}/products/categories/${slug}`;

  const description = descricao
    ? truncateText(descricao, 155)
    : `Explore nossa coleção de ${categoria}. Lingerie sexy e sensual com qualidade. Embalagem Discreta. 5% De Desconto No Pix. Até 6x Sem Juros.`;

  const keywords = generateKeywords(`${categoria} ${descricao}`, [
    "sensualle",
    "lingerie",
    "sexy",
    "sensual",
    categoria.toLowerCase(),
  ]);

  return {
    title: `${categoria} | Sensualle Lingerie Sexy`,
    description,
    keywords,
    canonical,
    robots: "index, follow",
    author: "Sensualle Lingerie Sexy",
    publisher: "Sensualle Lingerie Sexy",
    ogTitle: `${categoria} - Sensualle`,
    ogDescription: description,
    ogUrl: canonical,
  };
};

/**
 * Configuração SEO para página principal de produtos
 */
export const generateProductsListSeo = () => {
  const baseUrl = window.location.origin;
  const canonical = `${baseUrl}/products`;

  return {
    title: "Catálogo de Produtos | Sensualle Lingerie Sexy",
    description:
      "Explore nosso catálogo completo de lingerie sexy e sensual. Qualidade, conforto e sedução em cada peça. Embalagem Discreta. 5% De Desconto No Pix. Até 6x Sem Juros.",
    keywords:
      "lingerie, lingerie sexy, lingerie sensual, moda íntima, sensualle, catálogo lingerie, comprar lingerie, lingerie feminina, lingerie erótica",
    canonical,
    robots: "index, follow",
    author: "Sensualle Lingerie Sexy",
    publisher: "Sensualle Lingerie Sexy",
    ogTitle: "Catálogo de Lingerie | Sensualle",
    ogDescription:
      "Explore nosso catálogo completo de lingerie sexy e sensual.",
    ogUrl: canonical,
  };
};

/**
 * Configuração SEO para página de busca
 */
export const generateSearchSeo = (termoBusca) => {
  const baseUrl = window.location.origin;
  const slug = createSlug(termoBusca);
  const canonical = `${baseUrl}/products/search/${slug}`;

  return {
    title: `Busca: ${termoBusca} | Sensualle Lingerie Sexy`,
    description: `Resultados da busca por "${termoBusca}" na Sensualle. Encontre lingerie sexy e sensual com qualidade.`,
    keywords: `${termoBusca}, lingerie, sensualle, busca lingerie`,
    canonical,
    robots: "index, follow",
    author: "Sensualle Lingerie Sexy",
    publisher: "Sensualle Lingerie Sexy",
  };
};
